/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"
import { formatTimestamp } from "@/utils/format-timestamp"
import type { ApplicationEvent } from "../../features/application-detail/application-detail-types"
import type { ApplicationDetailViewModel } from "../../features/application-detail/application-detail-viewmodel"
import { formatEventParameters } from "../../features/application-detail/format-event-parameters"
import { formatEnum } from "../../utils/format-enum"
import { valueOrDash } from "../../utils/value-or-dash"
import { Card } from "../primitives/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../primitives/table"
import { LoadMoreButton } from "./load-more-button"

interface Props {
    /**
     * View model that owns the loaded event pages and loads additional pages.
     */
    vm: ApplicationDetailViewModel
}

/**
 * Displays the device's events, newest first, with incremental pagination.
 *
 * Already loaded pages remain visible while Load More appends the next page.
 * The button disappears once all events have been loaded.
 *
 * @example
 * <TabsContent value="events">
 *     <ApplicationDetailEvents vm={vm} />
 * </TabsContent>
 */
export const ApplicationDetailEvents = observer(({ vm }: Props) => {
    const events = vm.applicationEvents

    return (
        <div className="grid gap-4 border bg-background px-3 py-6">
            <h3 className="m-0 text-xl font-semibold">Security Events</h3>

            <Card className="py-0">
                {/* Rows are not interactive, so don't show a hover state. */}
                <Table className="[&_tr]:hover:bg-transparent">
                    <colgroup>
                        <col className="w-17/100" />
                        <col className="w-33/100" />
                        <col className="w-1/10" />
                        <col className="w-1/10" />
                        <col className="w-3/10" />
                    </colgroup>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Created</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Category</TableHead>
                            <TableHead>Severity</TableHead>
                            <TableHead>Parameters</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>{events.length > 0 ? events.map(renderEvent) : renderEmptyRow()}</TableBody>
                </Table>
            </Card>

            {!vm.eventsLoaded && <LoadMoreButton onLoad={() => vm.appendApplicationEvent()}>Load More</LoadMoreButton>}
        </div>
    )
})

/**
 * Renders one event as a table row.
 *
 * Enum fields are displayed as sentence-case labels. The final cell contains
 * the detector result and parameters, or a dash when neither is present.
 *
 * @param event - Event from one of the loaded pages.
 * @returns The event row keyed by its ID.
 */
const renderEvent = (event: ApplicationEvent) => (
    <TableRow key={event.id}>
        <TableCell className="whitespace-normal tabular-nums">
            {valueOrDash(formatTimestamp(event.timestampCreated))}
        </TableCell>
        <TableCell className="whitespace-normal">{formatEnum(event.name)}</TableCell>
        <TableCell>{formatEnum(event.category)}</TableCell>
        <TableCell>{formatEnum(event.severity)}</TableCell>
        {/* Keep the result and each parameter on its own line. */}
        <TableCell className="font-mono text-xs wrap-anywhere whitespace-pre-line">
            {valueOrDash(formatEventParameters(event))}
        </TableCell>
    </TableRow>
)

/**
 * Renders the empty state when no events are available.
 *
 * The same row is shown while the first page is loading because the table does
 * not distinguish an empty result from an unloaded one.
 *
 * @returns A row spanning all event columns.
 */
function renderEmptyRow() {
    return (
        <TableRow>
            <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                No events
            </TableCell>
        </TableRow>
    )
}
